<template>
  <BaseModal :visible="true" title="Facturar venta" max-width="max-w-[760px]" persistent @close="cerrar">
    <p class="factura-contexto">Venta {{ venta.serial_sale }} · {{ venta.client && venta.client.full_name }}</p>
    <p v-if="cargando" role="status">Cargando datos de facturación…</p>
    <div v-if="errorCarga" role="alert" class="factura-error">No pudimos cargar las opciones fiscales. <button type="button" @click="cargarOpciones">Reintentar</button></div>
    <p v-if="errorGuardado" role="alert" class="factura-error" data-testid="factura-error">{{ errorGuardado }}</p>
    <form class="factura-form" novalidate data-testid="factura-form" @submit.prevent="guardar">
      <AppField v-slot="{ controlId }" label="Cliente registrado" class="factura-ancho">
        <AppCombobox :id="controlId" :value="receptorId" :options="opcionesReceptor" placeholder="Busca por razón social o RFC" testid="factura-receptor" @input="elegirReceptor" />
      </AppField>
      <AppField v-for="campo in camposReceptor" :key="campo.key" v-slot="{ controlId }" :label="campo.label" :required="esRequerido(campo.key)" :error="mostrarError(campo.key)" :testid="'factura-' + campo.key">
        <input :id="controlId" class="app-field-control" :type="campo.type || 'text'" :value="receptorActual[campo.key]" :disabled="Boolean(receptorId)" :data-testid="'factura-' + campo.key" :aria-invalid="mostrarError(campo.key) ? 'true' : null" :aria-describedby="mostrarError(campo.key) ? controlId + '-error' : null" @input="cambiarCampo(campo, $event)" @blur="tocar(campo.key)" />
      </AppField>
      <AppField v-slot="{ controlId }" label="Régimen fiscal" required :error="mostrarError('regimen_fiscal')" testid="factura-regimen_fiscal">
        <select :id="controlId" v-model="receptorActual.regimen_fiscal" class="app-field-control" :disabled="esPublico" data-testid="factura-regimen_fiscal" :aria-invalid="mostrarError('regimen_fiscal') ? 'true' : null" :aria-describedby="mostrarError('regimen_fiscal') ? controlId + '-error' : null" @blur="tocar('regimen_fiscal')">
          <option :value="null">Seleccionar</option>
          <option v-for="regimen in regimenes" :key="regimen.value" :value="regimen.value">{{ regimen.text }}</option>
        </select>
      </AppField>
      <h4 class="factura-ancho">Parámetros de facturación</h4>
      <AppField v-if="tieneManoObra" v-model="manoObra" label="Concepto de mano de obra" class="factura-ancho" testid="factura-mano-obra" />
      <AppField v-for="campo in camposFiscales" :key="campo.key" v-slot="{ controlId }" :label="campo.label" :required="campo.key !== 'tipo_cfdi' || !esPublico" :error="mostrarError(campo.key)" :testid="'factura-' + campo.key">
        <select :id="controlId" v-model="form[campo.key]" class="app-field-control" :disabled="esPublico && ['uso_cfdi', 'metodo_pago'].includes(campo.key)" :data-testid="'factura-' + campo.key" :aria-invalid="mostrarError(campo.key) ? 'true' : null" :aria-describedby="mostrarError(campo.key) ? controlId + '-error' : null" @blur="tocar(campo.key)">
          <option :value="null">Seleccionar</option>
          <option v-for="opcion in opciones[campo.key]" :key="opcion.clave" :value="opcion.clave">{{ opcion.nombre }}</option>
        </select>
      </AppField>
      <AppField v-slot="{ controlId }" label="Observaciones" class="factura-ancho">
        <textarea :id="controlId" v-model="form.observacion" class="app-field-control" rows="3" data-testid="factura-observacion"></textarea>
      </AppField>
      <label class="factura-checkbox factura-ancho"><input v-model="form.aplicaIVA" type="checkbox" data-testid="factura-iva" /> Declarar impuestos</label>
    </form>
    <template #footer>
      <button type="button" class="rm-btn rm-btn-secundario" :disabled="guardando" @click="cerrar">Cancelar</button>
      <button type="button" class="rm-btn rm-btn-primario" :disabled="guardando || cargando || errorCarga" data-testid="factura-generar" @click="guardar">{{ guardando ? 'Generando…' : 'Generar factura' }}</button>
    </template>
  </BaseModal>
</template>

<script>
import BaseModal from '@/components/ui/BaseModal.vue'
import AppCombobox from '@/components/ui/AppCombobox.vue'
import AppField from '@/components/ui/AppField.vue'
import ApiService from '@/services/apis'
import Catalogos from '@/services/catalogos'
import { validateNoErrors } from '@/helpers/helpers'

const receptorVacio = () => ({ razon_social: null, rfc: null, email: null, name: null, second_lastname: null, first_lastname: null, telephones: null, regimen_fiscal: null, codigo_postal: null, calle: null, numero_exterior: null, numero_interior: null, colonia: null, municipio: null, estado: null, pais: 'México' })
const formulario = venta => ({ sale_id: venta._id, receptor_id: null, aplicaIVA: false, receptor: receptorVacio(), uso_cfdi: null, tipo_cfdi: 'I', forma_pago: null, metodo_pago: null, logo: null, user: null, observacion: venta.concepto || '' })
const camposReceptor = [
  { key: 'name', label: 'Nombre' }, { key: 'first_lastname', label: 'Apellido paterno' }, { key: 'second_lastname', label: 'Apellido materno' },
  { key: 'telephones', label: 'Teléfono', type: 'tel' }, { key: 'rfc', label: 'RFC', uppercase: true }, { key: 'razon_social', label: 'Razón social', uppercase: true },
  { key: 'email', label: 'Correo', type: 'email' }, { key: 'codigo_postal', label: 'Código postal' }, { key: 'calle', label: 'Calle', uppercase: true },
  { key: 'numero_exterior', label: 'Número exterior' }, { key: 'numero_interior', label: 'Número interior' }, { key: 'colonia', label: 'Colonia', uppercase: true },
  { key: 'municipio', label: 'Municipio', uppercase: true }, { key: 'estado', label: 'Estado', uppercase: true }, { key: 'pais', label: 'País', uppercase: true },
]
const regimenes = [

      { value: 601, text: "General de Ley Personas Morales" },
      { value: 603, text: "Personas Morales con Fines no Lucrativos" },
      {
        value: 605,
        text: "Sueldos y Salarios e Ingresos Asimilados a Salarios",
      },
      { value: 606, text: "Arrendamiento" },
      { value: 607, text: "Régimen de Enajenación o Adquisición de Bienes" },
      { value: 608, text: "Demás ingresos" },
      { value: 609, text: "Consolidación" },
      {
        value: 610,
        text: "Residentes en el Extranjero sin Establecimiento Permanente en México",
      },
      { value: 611, text: "Ingresos por Dividendos (socios y accionistas)" },
      {
        value: 612,
        text: "Personas Físicas con Actividades Empresariales y Profesionales",
      },
      { value: 614, text: "Ingresos por intereses" },
      { value: 615, text: "Régimen de los ingresos por obtención de premios" },
      { value: 616, text: "Sin obligaciones fiscales" },
      {
        value: 620,
        text: "Sociedades Cooperativas de Producción que optan por diferir sus ingresos",
      },
      { value: 621, text: "Incorporación Fiscal" },
      {
        value: 622,
        text: "Actividades Agrícolas, Ganaderas, Silvícolas y Pesqueras",
      },
      { value: 623, text: "Opcional para Grupos de Sociedades" },
      { value: 624, text: "Coordinados" },
      {
        value: 625,
        text: "Régimen de las Actividades Empresariales con ingresos a través de Plataformas Tecnológicas",
      },
      { value: 626, text: "Régimen Simplificado de Confianza" },
      { value: 628, text: "Hidrocarburos" },
      {
        value: 629,
        text: "De los Regímenes Fiscales Preferentes y de las Empresas Multinacionales",
      },
      { value: 630, text: "Enajenación de acciones en bolsa de valores" },
]
const tipos = [{ clave: 'I', nombre: 'Comprobante de Ingreso' }, { clave: 'E', nombre: 'Comprobante de Egreso' }, { clave: 'T', nombre: 'Comprobante de Traslado' }, { clave: 'N', nombre: 'Comprobante de Nómina' }, { clave: 'P', nombre: 'Comprobante de Pago' }]

export default {
  name: 'FacturaVenta',
  components: { BaseModal, AppCombobox, AppField },
  props: { venta: { type: Object, required: true } },
  data() {
    return { form: formulario(this.venta), receptorId: '', rfc: null, receptores: [], regimenes, camposReceptor, manoObra: '',
      opciones: { uso_cfdi: [], metodo_pago: [], forma_pago: [], tipo_cfdi: tipos },
      camposFiscales: [{ key: 'uso_cfdi', label: 'Concepto de pago' }, { key: 'metodo_pago', label: 'Método de pago' }, { key: 'forma_pago', label: 'Forma de pago' }, { key: 'tipo_cfdi', label: 'Tipo de comprobante' }],
      cargando: true, errorCarga: false, guardando: false, errorGuardado: '', intentoGuardar: false, tocados: {},
    }
  },
  computed: {
    receptorActual() { return this.rfc || this.form.receptor },
    // Con buscador, como el v-autocomplete del sistema anterior: en un <select> no se puede escribir
    opcionesReceptor() { return [{ value: '', label: 'Capturar nuevo receptor' }, ...this.receptores.map(r => ({ value: r._id, label: `${r.razon_social} · ${r.rfc}` }))] },
    esPublico() { return this.receptorActual.rfc === 'XAXX010101000' },
    tieneManoObra() { return (this.venta.sale_detail || []).some(item => item.type == 2 && String(item.service?.name || '').includes('MANO DE OBRA')) },
    requeridos() {
      const campos = ['razon_social', 'regimen_fiscal', 'uso_cfdi', 'metodo_pago', 'forma_pago']
      if (!this.esPublico) campos.push('codigo_postal', 'calle', 'numero_exterior', 'colonia', 'municipio', 'estado', 'pais', 'tipo_cfdi')
      if (!this.receptorId) campos.push('name', 'first_lastname', 'telephones')
      if (!this.receptorId && !this.esPublico) campos.push('second_lastname')
      return campos
    },
    errores() {
      const e = {}, r = this.receptorActual
      this.requeridos.forEach(campo => { if (!(campo in this.form ? this.form[campo] : r[campo])) e[campo] = 'Requerido.' })
      if (!validateNoErrors('email', r.email)) e.email = 'Correo no válido.'
      if (!validateNoErrors('rfc', r.rfc)) e.rfc = 'RFC no válido.'
      // Conservar ambas ramas legadas: nuevo público no comprobaba regex de razón social.
      if ((this.receptorId || !this.esPublico) && !validateNoErrors('razonSocial', r.razon_social)) e.razon_social = 'Razón social no válida.'
      if (!this.receptorId && !this.esPublico && !validateNoErrors('telephones', r.telephones)) e.telephones = 'Deben ser 10 dígitos.'
      return e
    },
  },
  watch: { 'form.receptor.rfc'() { this.aplicarPublico() } },
  created() { this.cargarOpciones() },
  methods: {
    async cargarOpciones() {
      if (this.cargando && this.consultaIniciada) return
      this.consultaIniciada = true; this.cargando = true; this.errorCarga = false
      try {
        const [cfdi, metodos, formas, receptores] = await Promise.all([ApiService.ConceptoPago(), ApiService.MetodosPago(), ApiService.FormaPago(), ApiService.RFC()])
        if (![cfdi, metodos, formas, receptores].every(Array.isArray)) throw new Error('Opciones inválidas')
        this.opciones = { uso_cfdi: cfdi, metodo_pago: metodos, forma_pago: formas, tipo_cfdi: tipos }; this.receptores = receptores
      } catch (error) { this.errorCarga = true } finally { this.cargando = false }
    },
    elegirReceptor(id) { this.receptorId = id; this.seleccionarReceptor() },
    seleccionarReceptor() {
      const actual = this.receptores.find(r => r._id === this.receptorId)
      this.rfc = actual ? { ...actual, telephones: Array.isArray(actual.telephones) ? actual.telephones[0] : actual.telephones } : null
      this.aplicarPublico(); this.tocados = {}; this.intentoGuardar = false; this.errorGuardado = ''
    },
    aplicarPublico() {
      if (this.esPublico) {
        this.form.uso_cfdi = 'S01'; this.form.metodo_pago = 'PUE'; this.form.receptor.regimen_fiscal = 616
        if (this.rfc) this.rfc.regimen_fiscal = 616
      }
    },
    cambiarCampo(campo, event) {
      const valor = campo.uppercase ? event.target.value.toUpperCase() : event.target.value
      event.target.value = valor; this.form.receptor[campo.key] = valor
    },
    esRequerido(campo) { return this.requeridos.includes(campo) || ['email', 'rfc'].includes(campo) },
    tocar(campo) { this.tocados = { ...this.tocados, [campo]: true } },
    mostrarError(campo) { return this.intentoGuardar || this.tocados[campo] ? this.errores[campo] || '' : '' },
    cerrar() { if (!this.guardando) this.$emit('close') },
    async guardar() {
      if (this.guardando || this.cargando || this.errorCarga) return
      this.intentoGuardar = true
      if (Object.keys(this.errores).length) {
        await this.$nextTick(); this.$el.querySelector('[aria-invalid="true"]')?.focus(); return
      }
      this.guardando = true; this.errorGuardado = ''
      try {
        const payload = { ...this.form, receptor: { ...this.form.receptor }, receptor_id: this.receptorId || null,
          user: JSON.parse(sessionStorage.getItem('sesion')).employee._id, logo: 'https://sistema.automotrizrm.com/img/favicon.5c9a3179.png' }
        if (this.rfc) { Object.keys(payload.receptor).forEach(campo => { payload.receptor[campo] = this.rfc[campo] }); payload.manoObra = this.manoObra }
        await Catalogos.crearFactura(payload)
        this.$emit('facturada')
      } catch (error) { this.errorGuardado = error?.data?.message || error?.response?.data?.message || (typeof error?.data === 'string' ? error.data : '') || 'No pudimos generar la factura. Conservamos los datos para reintentar.' } finally { this.guardando = false }
    },
  },
}
</script>

<style scoped>
.factura-contexto { margin-bottom: 18px; color: #607187; }
.factura-form { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 14px; }
.factura-ancho { grid-column: 1 / -1; }
.factura-error { color: #b91c1c; margin-bottom: 14px; }
.factura-error button { min-height: 44px; text-decoration: underline; }
.factura-checkbox { display: flex; align-items: center; gap: 10px; min-height: 44px; }
.factura-checkbox input { width: 20px; height: 20px; }
textarea.app-field-control { padding: 10px 12px; resize: vertical; }
@media (max-width: 700px) { .factura-form { grid-template-columns: minmax(0, 1fr); } }
</style>
